import { useCallback, useEffect, useState } from 'react';

import {
  getUserInsightById,
  updateUserInsight,
} from 'api/generated/users-insight/users-insight';
import type { UserInsightDto } from 'api/generated/schemas/userInsightDto';

import { useCurrentUser } from 'state/current-user/current-user.thunk';

export type InsightDetail = UserInsightDto;

interface UseInsightDetailsArgs {
  insightId: string | number | undefined;
}

interface UseInsightDetailsResult {
  insight: InsightDetail | null;
  loading: boolean;
  error: unknown;
  refetch: () => Promise<void>;
  markInsightDownloaded: () => Promise<void>;
}

export const useInsightDetails = ({
  insightId,
}: UseInsightDetailsArgs): UseInsightDetailsResult => {
  const [insight, setInsight] = useState<InsightDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<unknown>(null);

  const currentUser = useCurrentUser();
  const userId = currentUser?.data?.oktaUserId;
  const id = currentUser?.data?.id;

  const loadInsight = async (): Promise<void> => {
    if (!userId || !id || !insightId) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await getUserInsightById(userId, Number(id), Number(insightId));

      setInsight(data ?? null);
    } catch (err) {
      console.error('Error loading insight details:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInsight();
  }, [userId, id, insightId]);

  useEffect(() => {
    const markAsViewed = async (): Promise<void> => {
      if (insight && !insight.viewed && userId && id && insightId) {
        try {
          await updateUserInsight(userId, Number(id), Number(insightId), {
            viewed: true,
          });

          setInsight((prev) => (prev ? { ...prev, viewed: true } : prev));

          (
            currentUser as unknown as { decrementInsightsCount: () => void }
          ).decrementInsightsCount();
        } catch (err) {
          console.error('Error marking insight as viewed:', err);
        }
      }
    };

    markAsViewed();
  }, [insight?.id, userId, id, insightId]);

  const markInsightDownloaded = useCallback(async (): Promise<void> => {
    if (!userId || !id || !insightId) return;

    try {
      await updateUserInsight(userId, Number(id), Number(insightId), {
        downloaded: true,
      });
      setInsight((prev) => (prev ? { ...prev, downloaded: true } : prev));
    } catch (err) {
      console.error('Error marking insight as downloaded:', err);
    }
  }, [userId, id, insightId]);

  return {
    insight,
    loading,
    error,
    refetch: loadInsight,
    markInsightDownloaded,
  };
};
